import { defineStore } from 'pinia';
import { reactive } from 'vue';
import { ITrashBinState } from './interfaces/trashBin';
import { TableContentHeader, TableContentProperty } from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { GetTrashBinAggregateBusinessUseCase } from '../useCases/GetTrashBinAggregateBusinessUseCase';
import { IAggregateBusiness } from '../domains/entities/aggregateBusiness';
import { TableContentItem } from '@/corePagesModule/BaseTableContent/interface/tableContentItems';
import { GetTrashBinBusinessUseCase } from '../useCases/GetTrashBinBusinessUseCase';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { DeleteTrashBinAggregateBusinessUseCase } from '../useCases/DeleteTrashBinAggregateBusiness';
import { DeleteTrashBinBusinessUseCase } from '../useCases/DeleteTrashBinBusiness';
import { UpdateTrashBinAggregateBusinessUseCase } from '../useCases/UpdateTrashBinAggregateBusinessUseCase';
import { UpdateTrashBinBusinessUseCase } from '../useCases/UpdateTrashBinBusiness';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { GetTrashBinAllAggregateBusinessUseCase } from '../useCases/GetTrashBinAllAggregateBusinessUseCase';
import { GetTrashBinAllBusinessUseCase } from '../useCases/GetTrashBinAllBusinessUseCase';
import { TrashBinRepository } from '@/coreInfrastructureModule/Repositories/TrashBinRepository';
import { HEADER_MODIFY_TABLE_CONTENT_PROPERTY } from '@/corePagesModule/BaseTableContent/constants';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { simpleCopy } from '@/utils/deepCopy';
import { loadPreferences, myPreferences, savePreferences } from '@/appLogic/view/preferences';
import { TRASH_BIN_TABLE_TYPE } from '@/trashBinModule/constants';

export const useTrashBinStore = defineStore('useTrashBin', () => {
  const state = reactive<ITrashBinState>({
    aggregateBusinessItems: [],
    businessItems: [],
    tableContentProperties: {} as TableContentHeader,
    tableContentItems: [],
    loadingState: {
      status: {
        type: 'loading',
      },
    },
    checkedIds: [],
    sortKey: undefined,
    isAsc: true,
    nowView: 'aggregateBusiness',
    pagingOptions: initPagingOptions(30),
    filterModalItems: {
      selectionFilterItems: [],
    },
    isDeleteAggregateBusinessModalOpened: false,
    isDeleteBusinessModalOpened: false,
    isUpdateModalOpened: false,
    isErrorModalOpened: false,
    isAllSelected: false,
  });

  /**
   * stateの初期化
   */
  const $reset = () => {
    state.aggregateBusinessItems = [];
    state.businessItems = [];
    state.loadingState = {
      status: {
        type: 'loading',
      },
    };
    state.pagingOptions = initPagingOptions(30);
    state.tableContentItems = [];
    state.tableContentProperties = {} as TableContentHeader;
    state.checkedIds = [];
    state.sortKey = undefined;
    state.isAsc = true;
    state.nowView = 'aggregateBusiness';
    state.filterModalItems = {
      selectionFilterItems: [],
    };
    state.isDeleteAggregateBusinessModalOpened = false;
    state.isDeleteBusinessModalOpened = false;
    state.isUpdateModalOpened = false;
    state.isErrorModalOpened = false;
    state.isAllSelected = false;
  };

  // for Debug
  const trashBinRepository = new TrashBinRepository();

  /**
   * ゴミ箱データの取得
   * @param nowView - 現在の表示タブ
   */
  const fetchItems = async (nowView: string) => {
    state.loadingState = {
      status: {
        type: 'loading',
      },
    };

    // 永続化された設定を読み込む
    await loadPreferences();
    const savedPreferences = myPreferences[TRASH_BIN_TABLE_TYPE];

    if (savedPreferences) {
      // ソート設定の適用
      if (savedPreferences.sortKey) {
        state.sortKey = savedPreferences.sortKey;
      }
      if (savedPreferences.isAsc !== undefined) {
        state.isAsc = savedPreferences.isAsc;
      } else {
        state.isAsc = true; // デフォルト値
      }

      // ページサイズ設定の適用
      if (savedPreferences.pageSize) {
        if (state.pagingOptions) {
          state.pagingOptions.pageSize = savedPreferences.pageSize;
        } else {
          // pagingOptionsがnullishの場合のフォールバック
          state.pagingOptions = { ...initPagingOptions(savedPreferences.pageSize), pageSize: savedPreferences.pageSize };
        }
      }
    }

    // UIのソート表示を更新 (fetchItemsの既存ロジックに合わせる)
    if (state.sortKey) {
      state.tableContentProperties.contentProperties = state.tableContentProperties.contentProperties.map((property) => {
        const currentIsAsc = state.isAsc ?? true;
        // property.key (UI key) をバックエンドキーに変換して state.sortKey (バックエンドキー) と比較
        const backendPropertyKey = HeaderKeyToColumn[property.key] || property.key; // マッピングがあれば変換、なければそのまま
        property.sortOption.isAsc = backendPropertyKey === state.sortKey ? currentIsAsc : true; // ソート対象外は上向き
        return property;
      });
    }
    switch (nowView) {
      case 'aggregateBusiness':
        const getAggregateBusinessResult = await GetTrashBinAggregateBusinessUseCase.execute(
          trashBinRepository,
          state.isAsc ? 'ASC' : 'DESC',
          state.pagingOptions.pageSize,
          state.pagingOptions.offset,
          state.sortKey,
          []
        );

        switch (getAggregateBusinessResult.status.type) {
          case 'success':
            const [items, pagingOptions] = getAggregateBusinessResult.status.data;
            state.aggregateBusinessItems = items;
            state.pagingOptions = pagingOptions;
            state.tableContentItems = convertAggregateBusinessToTableContentItems(state.aggregateBusinessItems);

            if ((pagingOptions?.returnCount ?? 0) === 0) {
              state.loadingState.status.type = 'zeroMatch';
            } else {
              state.loadingState.status.type = 'success';
            }
            break;
          case 'error':
            state.loadingState = {
              status: {
                type: 'error',
                errorMessage: '取得に失敗しました', // TODO: 本来はresult.status.error.message
              },
            };
            break;
        }
        break;

      case 'business':
        const getBusinessResult = await GetTrashBinBusinessUseCase.execute(
          trashBinRepository,
          state.isAsc ? 'ASC' : 'DESC',
          state.pagingOptions.pageSize,
          state.pagingOptions.offset,
          state.sortKey,
          []
        );

        switch (getBusinessResult.status.type) {
          case 'success':
            const [items, pagingOptions] = getBusinessResult.status.data;
            state.businessItems = items;
            state.pagingOptions = pagingOptions;
            state.tableContentItems = convertBusinessToTableContentItems(state.businessItems);

            if ((pagingOptions?.returnCount ?? 0) === 0) {
              state.loadingState.status.type = 'zeroMatch';
            } else {
              state.loadingState.status.type = 'success';
            }
            break;
          case 'error':
            state.loadingState = {
              status: {
                type: 'error',
                errorMessage: '取得に失敗しました', // TODO: 本来はresult.status.error.message
              },
            };
            break;
        }
    }
  };

  /**
   * フィルターモーダルの適用ボタンクリック時の処理
   * @param filterItems - フィルターモーダル内で選択された項目
   */
  const onAppliedFilterModal = async (filterItems: FilterContentItem[]) => {
    // フィルターモーダルの選択情報を更新
    state.filterModalItems.selectionFilterItems = simpleCopy(filterItems);
    // オフセットをリセットしてから表示内容を更新
    state.pagingOptions = initPagingOptions(30);
    await fetchItems(state.nowView);
  };

  /**
   * テーブルのデータをソートする
   * @param sortKey - ソートキー
   * @param isAsc - 昇順かどうか
   */
  const sortTableContentItems = async (sortKey: TableContentProperty, isAsc: boolean) => {
    const newIsAsc = sortKey.key === state.sortKey ? !state.isAsc : false;
    state.sortKey = HeaderKeyToColumn[sortKey.key] ?? undefined;
    state.isAsc = newIsAsc;

    // ソート設定の永続化対応：IndexedDBに保存
    await loadPreferences();
    if (!myPreferences[TRASH_BIN_TABLE_TYPE]) {
      myPreferences[TRASH_BIN_TABLE_TYPE] = {};
    }
    myPreferences[TRASH_BIN_TABLE_TYPE].sortKey = state.sortKey;
    myPreferences[TRASH_BIN_TABLE_TYPE].isAsc = state.isAsc;
    await savePreferences();

    fetchItems(state.nowView);
  };

  /**
   * テーブルのデータを再取得する
   */
  const reloadTable = async () => {
    await fetchItems(state.nowView);
    fetchTableContentProperties();
  };

  /**
   * 集計情報をテーブル表示用の情報に変換する
   * @param aggregateBusinesses - 集計情報の配列
   * @returns - テーブル表示用の情報
   */
  const convertAggregateBusinessToTableContentItems = (aggregateBusinesses: Array<IAggregateBusiness>): Array<TableContentItem> => {
    const displayTableContent: Array<TableContentItem> = [];
    aggregateBusinesses.forEach((aggregateBusiness) => {
      displayTableContent.push({
        displayId: aggregateBusiness.aggregateBusinessId,
        isVirtualChild: false,
        ...aggregateBusiness,
      });
    });
    return displayTableContent;
  };

  /**
   * 取引情報をテーブル表示用の情報に変換する
   * @param businesses - 集計情報の配列
   * @returns - テーブル表示用の情報
   */
  const convertBusinessToTableContentItems = (businesses: Array<IBusiness>): Array<TableContentItem> => {
    const displayTableContent: Array<TableContentItem> = [];
    businesses.forEach((business) => {
      displayTableContent.push({
        displayId: business.businessId,
        isVirtualChild: false,
        ...business,
      });
    });
    return displayTableContent;
  };

  /**
   *  ページング変更時の処理
   * @param pagingOptions - ページングオプション
   */
  const onChangePaging = async (pagingOptions: IPagingOptions) => {
    state.pagingOptions = {
      pageSize: pagingOptions.pageSize,
      offset: pagingOptions.offset,
      totalResultsAvailable: pagingOptions.totalResultsAvailable,
      returnCount: pagingOptions.returnCount,
    };
    // ページサイズ設定の永続化対応：IndexedDBに保存
    await loadPreferences();
    if (!myPreferences[TRASH_BIN_TABLE_TYPE]) {
      myPreferences[TRASH_BIN_TABLE_TYPE] = {};
    }
    myPreferences[TRASH_BIN_TABLE_TYPE].pageSize = state.pagingOptions.pageSize;
    await savePreferences();
    fetchItems(state.nowView);
  };

  /**
   * テーブルのヘッダー情報を取得
   */
  const fetchTableContentProperties = (): TableContentHeader => {
    const res: TableContentHeader = {
      contentProperties: [
        {
          key: 'choice',
          isVariableWidth: false,
          isNarrowable: false,
          isEditable: false,
          sortOption: { isSortable: false, isAsc: true },
          headerWidth: 32,
          propertyPath: { paths: ['choice'], propertyType: 'string' },
        },
        {
          key: 'TrashBinWarehouseNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['warehouseNumber'], propertyType: 'string' },
        },
        {
          key: 'TrashBinWarehouse',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['warehouse'], propertyType: 'string' },
        },
        {
          key: 'TrashBinSupplierNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['supplierNumber'], propertyType: 'string' },
        },
        {
          key: 'TrashBinSupplierName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['supplierName'], propertyType: 'string' },
        },
        {
          key: 'TrashBinBusinessNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['businessNumber'], propertyType: 'number' },
        },
        {
          key: 'TrashBinBusinessName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['businessName'], propertyType: 'string' },
        },
        {
          key: 'TrashBinPackage',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['package'], propertyType: 'number' },
        },
        {
          key: 'TrashBinUnit',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['unit'], propertyType: 'string' },
        },
        {
          key: 'TrashBinCustomerNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['warehouse'], propertyType: 'string' },
        },
        {
          key: 'TrashBinCustomerName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['customerName'], propertyType: 'string' },
        },
        {
          key: 'TrashBinDeliveryDestinationNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['warehouse'], propertyType: 'string' },
        },
        {
          key: 'TrashBinDeliveryDestinationName',
          isVariableWidth: true,
          isNarrowable: false,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['salesDestinationName'], propertyType: 'string' },
        },
        {
          key: 'TrashBinManufacturer',
          isVariableWidth: true,
          isNarrowable: false,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['manufacturer'], propertyType: 'string' },
        },
        {
          key: 'TrashBinEndUser',
          isVariableWidth: true,
          isNarrowable: false,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['endUser'], propertyType: 'string' },
        },
        {
          key: 'TrashBinSupplierForecastResponsibleUser',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['AggregateBusinessResponsibleUser', 'name'], propertyType: 'string' },
        },
        {
          key: 'TrashBinCustomerForecastResponsibleUser',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['BaseBusinessResponsibleUser', 'name'], propertyType: 'string' },
        },
        {
          key: 'TrashBinDaysRemainUntilDeletion',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['daysRemainUntilDeletion'], propertyType: 'string' },
        },
        {
          key: 'TrashBinArrival',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: false,
            isAsc: true,
          },
          propertyPath: { paths: ['arrivalAmount'], propertyType: 'number' },
        },
        {
          key: 'TrashBinSalesResults',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: false,
            isAsc: true,
          },
          propertyPath: { paths: ['salesResults-1'], propertyType: 'number' },
        },
        {
          key: 'TrashBinLatestInventoryAmount',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: false,
            isAsc: true,
          },
          propertyPath: { paths: ['inventory'], propertyType: 'number' },
        },
        {
          key: 'TrashBinExecuteDate',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: false,
            isAsc: true,
          },
          propertyPath: { paths: ['executionDate'], propertyType: 'number' },
        },
        {
          key: 'TrashBinCustomerAnnounce-1',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: false,
            isAsc: true,
          },
          propertyPath: { paths: ['customerAnnounce-1'], propertyType: 'number' },
        },
        {
          key: 'TrashBinCustomerAnnounce-2',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: false,
            isAsc: true,
          },
          propertyPath: { paths: ['customerAnnounce-2'], propertyType: 'number' },
        },
        {
          key: 'TrashBinCustomerAnnounce-3',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: false,
            isAsc: true,
          },
          propertyPath: { paths: ['customerAnnounce-3'], propertyType: 'number' },
        },
        HEADER_MODIFY_TABLE_CONTENT_PROPERTY,
      ],
      options: {
        stableColumnCount: 1,
        resizableHeaderColumn: true,
      },
      forecastStartYearMonth: DateService.getShiftedIYearMonthFromNowDateByMonths(-1),
    };
    /*if (state.nowView === 'aggregateBusiness') {
      res.contentProperties.splice(1, 0, {
        key: 'expansion',
        isVariableWidth: false,
        isNarrowable: false,
        isEditable: false,
        sortOption: { isSortable: false, isAsc: true },
        headerWidth: 32,
        propertyPath: { paths: ['expansion'], propertyType: 'string' },
      });
    }*/
    return res;
  };

  /**
   * 削除対象の集計情報内の関連取引総数を取得する関数
   */
  const calcDeleteBusiness = () => {
    if (!(state.nowView === 'aggregateBusiness') || state.isAllSelected) return;
    return ''; // for debug
    const aggregateBusinessIds = state.checkedIds;
    let result = 0;
    aggregateBusinessIds.forEach((aggregateBusinessId) => {
      result++;
    });
    return result + '件';
  };

  /**
   * 集計情報の削除を行う関数
   */
  const deleteAggregateBusiness = async () => {
    const result = await DeleteTrashBinAggregateBusinessUseCase.Execute(trashBinRepository, state.checkedIds);
    if (result.status.type === 'error') {
      state.isErrorModalOpened = true;
    }
    state.isDeleteAggregateBusinessModalOpened = false;
    return;
  };

  /**
   * 取引情報の削除を行う関数
   */
  const deleteBusiness = async () => {
    const result = await DeleteTrashBinBusinessUseCase.Execute(trashBinRepository, state.checkedIds);
    if (result.status.type === 'error') {
      state.isErrorModalOpened = true;
    }
    state.isDeleteBusinessModalOpened = false;
    return;
  };

  /**
   * 集計/取引情報の更新を行う関数
   */
  const updateIsTrash = async () => {
    let result;
    switch (state.nowView) {
      case 'aggregateBusiness':
        result = await UpdateTrashBinAggregateBusinessUseCase.Execute(trashBinRepository, state.checkedIds, false);
        break;
      case 'business':
        result = await UpdateTrashBinBusinessUseCase.Execute(trashBinRepository, state.checkedIds, false);
    }
    if (result.status.type === 'error') {
      state.isErrorModalOpened = true;
    }
    state.isUpdateModalOpened = false;
    return;
  };

  /**
   * チェックされた際に行う処理
   * @param checked - テーブル上でチェックされた情報のID
   */
  const itemCheck = (checked: Array<string>) => {
    state.checkedIds = checked;
    return;
  };

  /**
   * 全ての取引をチェックした際に走る処理
   */
  const onClickTrashBinAllSelect = async () => {
    switch (state.nowView) {
      case 'aggregateBusiness':
        const getAggregateBusinessAllIdResult = await GetTrashBinAllAggregateBusinessUseCase.execute(trashBinRepository, []);

        switch (getAggregateBusinessAllIdResult.status.type) {
          case 'success':
            state.checkedIds = getAggregateBusinessAllIdResult.status.data;
            state.isAllSelected = true;
            break;
          case 'error':
            state.isErrorModalOpened = true;
            state.isUpdateModalOpened = false;
            break;
        }
        break;

      case 'business':
        const getBusinessResult = await GetTrashBinAllBusinessUseCase.execute(trashBinRepository, []);

        switch (getBusinessResult.status.type) {
          case 'success':
            state.checkedIds = getBusinessResult.status.data;
            state.isAllSelected = true;
            break;
          case 'error':
            state.isErrorModalOpened = true;
            state.isUpdateModalOpened = false;
            return;
        }
    }
    return;
  };

  return {
    state,
    $reset,
    sortTableContentItems,
    reloadTable,
    onChangePaging,
    fetchTableContentProperties,
    fetchItems,
    calcDeleteBusiness,
    deleteAggregateBusiness,
    deleteBusiness,
    updateIsTrash,
    itemCheck,
    onClickTrashBinAllSelect,
    onAppliedFilterModal,
  };
});

const HeaderKeyToColumn: Record<string, string> = {
  TrashBinWarehouseNumber: 'warehouseNumber',
  TrashBinWarehouse: 'warehouse',
  TrashBinSupplierNumber: 'supplierNumber',
  TrashBinSupplierName: 'supplierName',
  TrashBinManufacturer: 'manufacturer',
  TrashBinBusinessNumber: 'businessNumber',
  TrashBinBusinessName: 'businessName',
  TrashBinPackage: 'package',
  TrashBinUnit: 'unit',
  TrashBinSupplierForecastResponsibleUser: 'aggregateBusinessResponsibleUser',
  TrashBinCustomerForecastResponsibleUser: 'baseBusinessResponsibleUser',
  TrashBinCustomerNumber: 'customerNumber',
  TrashBinCustomerName: 'customerName',
  TrashBinDeliveryDestinationNumber: 'salesDestinationNumber',
  TrashBinDeliveryDestinationName: 'salesDestinationName',
  TrashBinEndUser: 'endUser',
  TrashBinDaysRemainUntilDeletion: 'updateAt',
};
